import { z } from 'zod';
import { ClaimType } from '~/utils/constants';
import { isLineItemInClaims } from '~/utils/line-item-claim';
import { isItemReturnEligible, isItemWarrantyEligible } from '~/utils/order';
import { makeCrewLoader } from '~/utils/routing';

type CrewContext = Parameters<
  Parameters<typeof makeCrewLoader>[0]
>[0]['context'];
type CrewOrderLineItem = NonNullable<CrewContext['order']>['lineItems'][number];

const sumPendingClaimQuantities = (claims: CrewContext['lineItemClaims']) =>
  claims.reduce((sums, { lineItem: { id }, quantity = 0 }) => {
    const currentSum = sums.get(id) ?? 0;
    sums.set(id, currentSum + quantity);
    return sums;
  }, new Map<number, number>());

const updateRemainingReturnableQuantity = (
  pendingClaims: CrewContext['lineItemClaims'],
) => {
  const pendingClaimQuantities = sumPendingClaimQuantities(pendingClaims);

  return (item: CrewOrderLineItem) => {
    const { currentQuantity, id } = item;
    const pendingClaimQuantity = pendingClaimQuantities.get(id) ?? 0;

    return {
      ...item,
      remainingReturnableQuantity: currentQuantity - pendingClaimQuantity,
    } satisfies CrewOrderLineItem;
  };
};

const paramsSchema = z.object({
  idFromPlatform: z.string(),
  claimType: z.nativeEnum(ClaimType),
});

export const { loader, useLoaderData: useOrderClaimData } = makeCrewLoader(
  ({ params, context }) => {
    const { claimType } = paramsSchema.parse(params);
    const {
      lineItemClaims: pendingClaims,
      settings: {
        return: { isEnabled: areReturnsEnabled },
      },
      order,
    } = context;

    const lineItems = order.lineItems.map(
      updateRemainingReturnableQuantity(pendingClaims),
    );

    const isEligible =
      claimType === ClaimType.warranty ?
        isItemWarrantyEligible
      : isItemReturnEligible(areReturnsEnabled);

    const eligibleLineItems = lineItems.filter(isEligible);
    const lastActionableDate =
      claimType === ClaimType.warranty ?
        order.warrantyUntil
      : order.returnUntil;

    return {
      lastActionableDate,
      claimType,
      lineItems: eligibleLineItems,
      pendingClaims,
      ineligibleItems: lineItems.filter(
        (item) =>
          !isEligible(item) && !isLineItemInClaims(item.id, pendingClaims),
      ),
    };
  },
);
